// src/utils/seoConfig.js
// Centralized SEO metadata for all state-specific routes.
//
// Usage:
//   const seo = getPageSeo('promotions', 'FL');

import { getStateContent } from '../config/stateContent';

const BASE_URL = 'https://boostyourbets.com';
const OG_IMAGE = 'https://boostyourbets.com/android-chrome-512x512.png';

function getStateName(stateCode) {
  if (!stateCode) return null;

  const content = getStateContent(stateCode);

  return content?.stateName || stateCode;
}

const PAGE_TEMPLATES = {
  home: (stateName, stateCode) => {
    const hasState = Boolean(stateName && stateCode);

    const canonical = hasState
      ? `${BASE_URL}/${stateCode.toLowerCase()}`
      : `${BASE_URL}/`;

    const title = hasState
      ? `${stateName} Sports Betting | Value Finder, Odds & Promotions | BoostYourBets`
      : 'BoostYourBets | Sports Betting Analytics & Positive EV Tools';

    const description = hasState
      ? `Explore ${stateName} sports betting analysis, positive-EV opportunities, sportsbook promotions, and real-time odds data from BoostYourBets.`
      : 'Find positive expected value bets, compare real-time odds across 20+ sportsbooks, and use data-driven tools to make smarter sports betting decisions.';

    return {
      title,
      description,
      canonical,
    };
  },

  'value-finder': (stateName, stateCode) => {
    const hasState = Boolean(stateName && stateCode);

    const canonical = hasState
      ? `${BASE_URL}/value-finder/${stateCode.toLowerCase()}`
      : `${BASE_URL}/value-finder`;

    const title = hasState
      ? `${stateName} Sports Betting Value Finder | Best Odds & +EV Opportunities`
      : 'Sports Betting Value Finder | Best Odds & +EV Opportunities';

    const description = hasState
      ? `Compare ${stateName} sportsbook odds, find the best available prices, and identify the highest expected-value (+EV) betting opportunities at ${stateName} sportsbooks.`
      : 'Compare sportsbook odds, find the best available prices, and identify the highest expected-value (+EV) betting opportunities across sportsbooks.';

    return {
      title,
      description,
      canonical,
    };
  },

  promotions: (stateName, stateCode) => {
    const hasState = Boolean(stateName && stateCode);

    const canonical = hasState
      ? `${BASE_URL}/promotions/${stateCode.toLowerCase()}`
      : `${BASE_URL}/promotions`;

    const title = hasState
      ? `${stateName} Sportsbook Promotions & Betting Bonuses | BoostYourBets`
      : 'Sportsbook Promotions & Betting Bonuses | BoostYourBets';

    const description = hasState
      ? `Compare ${stateName} sportsbook promotions, welcome bonuses, deposit matches, and promo codes. Find current betting offers available in ${stateName}.`
      : 'Compare sportsbook promotions, welcome bonuses, deposit matches, and promo codes across major US sportsbooks. Find current betting offers with BoostYourBets.';

    return {
      title,
      description,
      canonical,
    };
  },

  'promo-finder': (stateName, stateCode) => {
    const hasState = Boolean(stateName && stateCode);

    const canonical = hasState
      ? `${BASE_URL}/promo-finder/${stateCode.toLowerCase()}`
      : `${BASE_URL}/promo-finder`;

    const title = hasState
      ? `${stateName} Sportsbook Promo Finder | BoostYourBets`
      : 'Sportsbook Promo Finder | BoostYourBets';

    const description = hasState
      ? `Find and compare ${stateName} sportsbook bonus bets, profit boosts, free bets, and promotional offers. Use BoostYourBets to identify valuable ways to maximize sportsbook promotions.`
      : 'Find and compare sportsbook bonus bets, profit boosts, free bets, and promotional offers. Discover valuable sportsbook promotions with BoostYourBets.';

    return {
      title,
      description,
      canonical,
    };
  },

  'academy-ev-betting': () => {
    const canonical = `${BASE_URL}/academy/ev-betting`;
    const title = 'Expected Value (EV) Betting Explained | BoostYourBets';
    const description = 'Learn how expected value (EV), probability, and sportsbook odds work together to identify potentially profitable sports bets.';

    return {
      title,
      description,
      canonical,
    };
  },

  nfl: () => {
    const canonical = `${BASE_URL}/nfl`;
    const title = 'NFL Odds, Player Props & Super Bowl Odds | BoostYourBets';
    const description = 'Compare live NFL odds, Super Bowl futures, and anytime touchdown props across 20+ sportsbooks. Explore schedules, injuries, and +EV betting tools.';

    return {
      title,
      description,
      canonical,
    };
  },

  calculators: () => {
    const canonical = `${BASE_URL}/calculators`;
    const title = 'Betting Odds Calculator | American, Decimal & Percentage Converter | BoostYourBets';
    const description = 'Free betting calculators to convert American, Decimal, and Percentage odds, find implied probability, calculate no-vig expected value, and analyze parlay and same game parlay payouts.';

    return {
      title,
      description,
      canonical,
    };
  },
};

export function getPageSeo(pageType, stateCode) {
  const template = PAGE_TEMPLATES[pageType];

  if (!template) {
    throw new Error(
      `getPageSeo: unknown pageType "${pageType}"`
    );
  }

  const normalizedState = stateCode
    ? stateCode.toUpperCase()
    : null;

  const stateName = getStateName(normalizedState);

  const {
    title,
    description,
    canonical,
  } = template(
    stateName,
    normalizedState
  );

  return {
    title,
    description,
    canonical,
    ogUrl: canonical,
    ogTitle: title,
    ogDescription: description,
    ogImage: OG_IMAGE,
    twitterTitle: title,
    twitterDescription: description,
    robots: 'index, follow',
  };
}
